import { useEffect, useRef } from "react";

/**
 * useSnapNavigation — makes arrow keys / PageUp-Down / Space / wheel jump
 * between "snap points" in the page.
 *
 * Snap points are collected from any element marked [data-snap="true"],
 * plus the hero section, phase headers (start of each phase), and closing
 * section. This gives: hero → [phase1 title + 5 events] → [phase2 title + 6 events] → …
 *
 * Uses smooth scrolling via window.scrollTo({ behavior: 'smooth' }).
 * Wheel events are throttled so a single mouse notch advances one snap.
 */
export default function useSnapNavigation(enabled = true) {
  const busyRef = useRef(false);
  const lastWheelRef = useRef(0);

  useEffect(() => {
    if (!enabled) return undefined;

    const collectSnapPoints = () => {
      const nodes = Array.from(
        document.querySelectorAll(
          '[data-testid="hero-section"], [data-snap="true"], .phase-section, [data-testid="closing-section"]'
        )
      );
      // Map to absolute Y positions and dedupe within 10px
      const seen = new Set();
      const points = [];
      nodes.forEach((el) => {
        const y = Math.round(
          el.getBoundingClientRect().top + window.scrollY
        );
        if (y < 0) return;
        const bucket = Math.round(y / 10);
        if (seen.has(bucket)) return;
        seen.add(bucket);
        points.push(y);
      });
      points.sort((a, b) => a - b);
      return points;
    };

    const findNearest = (points, currentY, dir) => {
      // dir: +1 → next, -1 → previous
      const threshold = 12; // px tolerance
      if (dir > 0) {
        for (const p of points) {
          if (p > currentY + threshold) return p;
        }
        return points[points.length - 1];
      }
      let prev = points[0];
      for (const p of points) {
        if (p >= currentY - threshold) return prev;
        prev = p;
      }
      return prev;
    };

    const jump = (dir) => {
      if (busyRef.current) return;
      const points = collectSnapPoints();
      if (!points.length) return;
      const currentY = window.scrollY;
      const target = findNearest(points, currentY, dir);
      if (target == null || Math.abs(target - currentY) < 4) return;
      busyRef.current = true;
      window.scrollTo({ top: target, behavior: "smooth" });
      // Unlock after the smooth scroll finishes (roughly)
      window.setTimeout(() => {
        busyRef.current = false;
      }, 700);
    };

    const onKey = (e) => {
      // Skip when user is typing in a form
      const t = e.target;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.isContentEditable)
      ) {
        return;
      }

      if (e.key === "ArrowDown" || e.key === "PageDown") {
        e.preventDefault();
        jump(1);
      } else if (e.key === "ArrowUp" || e.key === "PageUp") {
        e.preventDefault();
        jump(-1);
      } else if (e.key === " " && !e.shiftKey) {
        e.preventDefault();
        jump(1);
      } else if (e.key === "Home") {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else if (e.key === "End") {
        e.preventDefault();
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        });
      }
    };

    const onWheel = (e) => {
      // Single-notch mouse wheel (typical |deltaY| >= 100) → jump one snap.
      // Trackpads produce many small deltas — leave them alone for free
      // scrolling. We also debounce per-direction.
      const now = performance.now();
      if (now - lastWheelRef.current < 700) return;
      if (Math.abs(e.deltaY) < 80) return;
      // Detect "discrete" wheel: high delta in one direction
      lastWheelRef.current = now;
      if (e.deltaY > 0) jump(1);
      else jump(-1);
    };

    window.addEventListener("keydown", onKey);
    // Wheel handler is opt-in: we listen but don't preventDefault so
    // trackpad users can still scroll freely. Only strong wheel notches
    // trigger a jump.
    window.addEventListener("wheel", onWheel, { passive: true });

    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("wheel", onWheel);
    };
  }, [enabled]);
}
